/* EasySlide: keep the existing desktop drawer and native mobile accordion.
   Translate only the known heading; also handle late app markup and section reloads. */
(() => {
  if (window.__fendoProductDetailsDrawerInitialized) return;
  window.__fendoProductDetailsDrawerInitialized = true;

  const desktopQuery = window.matchMedia('(min-width: 1000px)');
  const originalControls = new WeakMap();
  const initializedItems = new WeakSet();
  const sectionStates = new Map();
  const suspendedSections = new WeakSet();
  const decoder = document.createElement('textarea');
  const labelSelector = '.easyslider-header-text';
  const appSelector = '.easyslider-item, .easyslider-header, .easyslider-header-text';
  let drawer;
  let activeHeader;
  let activeSection;
  let openRequest = 0;

  const normalizeLabel = (value = '') => {
    decoder.innerHTML = value;
    return decoder.value.replace(/\s+/g, ' ').trim();
  };

  const setAttribute = (element, name, value) => {
    if (element && element.getAttribute(name) !== value) element.setAttribute(name, value);
  };

  const restoreActiveHeader = () => {
    if (!activeHeader) return;
    setAttribute(activeHeader, 'aria-expanded', 'false');
    const originalControl = originalControls.get(activeHeader);
    if (originalControl) setAttribute(activeHeader, 'aria-controls', originalControl);
    else activeHeader.removeAttribute('aria-controls');
    activeHeader = undefined;
    activeSection = undefined;
  };

  const onDrawerHidden = (event) => {
    if (event.target === drawer && !drawer.hasAttribute('open')) restoreActiveHeader();
  };

  const getDrawerContext = () => {
    const current = document.getElementById('fendo-product-details-drawer');
    if (current !== drawer) {
      drawer?.removeEventListener('dialog:after-hide', onDrawerHidden);
      openRequest += 1;
      restoreActiveHeader();
      drawer = current;
      drawer?.addEventListener('dialog:after-hide', onDrawerHidden);
    }
    const title = drawer?.querySelector('[data-fendo-product-details-title]');
    const body = drawer?.querySelector('[data-fendo-product-details-body]');
    if (!drawer || !title || !body) return null;
    return {
      drawer, title, body,
      // A comma separated list, so the merchant can maintain the app headings
      // that should be relabelled without a code change.
      fitSources: String(drawer.dataset.fendoFitSource || '')
        .split(',')
        .map(normalizeLabel)
        .filter(Boolean),
      fitLabel: normalizeLabel(drawer.dataset.fendoFitLabel)
    };
  };

  const closeActiveDrawer = () => {
    openRequest += 1;
    try {
      const hiding = drawer?.hide?.();
      hiding?.catch?.(() => {});
    } catch (error) { /* A section may already have been disconnected. */ }
    restoreActiveHeader();
  };

  const closeInlineItem = (item) => {
    const header = item.querySelector(':scope > .easyslider-header');
    const content = item.querySelector(':scope > .easyslider-content');
    if (item.classList.contains('easyslider-active')) item.classList.remove('easyslider-active');
    setAttribute(header, 'aria-expanded', 'false');
    if (content) {
      setAttribute(content, 'aria-hidden', 'true');
      if (content.style.height !== '0px') content.style.height = '0px';
    }
  };

  const translateLabel = (element, context) => {
    if (!context?.fitSources?.length || !context.fitLabel) return;

    const sources = context.fitSources.filter((source) => source !== context.fitLabel);
    if (!sources.length) return;
    if (!sources.includes(normalizeLabel(element.textContent))) return;

    // Keep existing spans/icons/listeners; edit text nodes only, never the detail content.
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    const nodes = [];
    let node;
    while ((node = walker.nextNode())) {
      if (!node.parentElement?.closest('svg, script, style')) nodes.push(node);
    }
    if (!nodes.length || !sources.includes(normalizeLabel(nodes.map((text) => text.data).join('')))) return;
    nodes.forEach((text, index) => {
      const value = index === 0 ? context.fitLabel : '';
      if (text.data !== value) text.data = value;
    });
    if (activeHeader?.contains(element)) context.title.textContent = context.fitLabel;
  };

  const synchronizeSection = (section) => {
    if (!section.isConnected || suspendedSections.has(section)) return;
    if (activeSection === section && !activeHeader?.isConnected) closeActiveDrawer();
    const context = getDrawerContext();
    const root = section.querySelector('.product-info') || section;
    root.querySelectorAll('.easyslider-item').forEach((item) => {
      if (initializedItems.has(item)) return;
      initializedItems.add(item);
      if (desktopQuery.matches) closeInlineItem(item);
    });
    root.querySelectorAll(labelSelector).forEach((label) => translateLabel(label, context));
  };

  const hasAppNode = (node) => node instanceof Element &&
    (node.matches(appSelector) || Boolean(node.querySelector(appSelector)));

  const relevantChanges = (records) => records.some((record) => {
    const target = record.target instanceof Element ? record.target : record.target.parentElement;
    if (target?.closest(labelSelector)) return true;
    return record.type === 'childList' &&
      (Array.from(record.addedNodes).some(hasAppNode) || Array.from(record.removedNodes).some(hasAppNode));
  });

  const scheduleSection = (section) => {
    const state = sectionStates.get(section);
    if (!state || state.frame !== null) return;
    state.frame = requestAnimationFrame(() => {
      state.frame = null;
      if (!section.isConnected) {
        disposeSection(section);
        return;
      }
      bindContentRoot(section, state);
      synchronizeSection(section);
    });
  };

  const bindContentRoot = (section, state) => {
    const root = section.querySelector('.product-info') || section;
    if (state.root === root) return;
    state.contentObserver?.disconnect();
    state.root = root;
    state.contentObserver = new MutationObserver((records) => {
      if (relevantChanges(records) || (activeSection === section && !activeHeader?.isConnected)) scheduleSection(section);
    });
    // No timers and no global body observer. Ignore class/style/ARIA changes.
    state.contentObserver.observe(root, { childList: true, subtree: true, characterData: true });
  };

  const disposeSection = (section) => {
    const state = sectionStates.get(section);
    if (!state) return;
    state.contentObserver?.disconnect();
    state.structureObserver.disconnect();
    if (state.frame !== null) cancelAnimationFrame(state.frame);
    sectionStates.delete(section);
    if (activeSection === section) closeActiveDrawer();
  };

  const registerSection = (section) => {
    if (!section.isConnected || suspendedSections.has(section)) return;
    if (sectionStates.has(section)) {
      scheduleSection(section);
      return;
    }
    const state = { root: null, contentObserver: null, structureObserver: null, frame: null };
    state.structureObserver = new MutationObserver(() => {
      // The product-info wrapper can be replaced by Prestige; rebind only then.
      if (state.root !== (section.querySelector('.product-info') || section)) scheduleSection(section);
    });
    sectionStates.set(section, state);
    bindContentRoot(section, state);
    state.structureObserver.observe(section, { childList: true, subtree: true });
    synchronizeSection(section);
  };

  const initialize = (root = document) => {
    sectionStates.forEach((state, section) => { if (!section.isConnected) disposeSection(section); });
    getDrawerContext();
    if (root instanceof Element && root.matches('.shopify-section--main-product')) registerSection(root);
    root.querySelectorAll?.('.shopify-section--main-product').forEach(registerSection);
  };

  const openDrawerForHeader = async (header, context) => {
    const item = header.closest('.easyslider-item');
    const source = item?.querySelector('.easyslider-content-wrapper');
    if (!item || !source) return;
    if (activeHeader && activeHeader !== header) restoreActiveHeader();
    if (!originalControls.has(header)) originalControls.set(header, header.getAttribute('aria-controls') || '');

    const request = ++openRequest;
    activeHeader = header;
    activeSection = header.closest('.shopify-section--main-product');
    closeInlineItem(item);
    setAttribute(header, 'aria-controls', context.drawer.id);
    setAttribute(header, 'aria-expanded', 'true');
    const label = header.querySelector(labelSelector);
    if (label) translateLabel(label, context);
    context.title.textContent = label?.textContent.trim() || header.textContent.trim();
    const clonedContent = source.cloneNode(true);
    clonedContent.classList.add('fendo-product-details-drawer__content');
    context.body.replaceChildren(clonedContent);

    await window.customElements.whenDefined('x-drawer');
    if (request !== openRequest || !header.isConnected || !desktopQuery.matches || !context.drawer.isConnected) return;
    await context.drawer.show();
  };

  const getDesktopHeader = (target) => {
    if (!desktopQuery.matches || !(target instanceof Element)) return null;
    const header = target.closest('.easyslider-header');
    return header?.closest('.shopify-section--main-product') ? header : null;
  };

  const handleActivation = (event) => {
    if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
    const header = getDesktopHeader(event.target);
    if (!header || !header.closest('.easyslider-item')?.querySelector('.easyslider-content-wrapper')) return;
    const context = getDrawerContext();
    if (!context) return; // Do not block the app's native interaction if the drawer is absent.
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    openDrawerForHeader(header, context).catch(() => {
      if (activeHeader === header) closeActiveDrawer();
    });
  };
  document.addEventListener('click', handleActivation, true);
  document.addEventListener('keydown', handleActivation, true);

  desktopQuery.addEventListener('change', () => {
    if (desktopQuery.matches) {
      document.querySelectorAll('.shopify-section--main-product .easyslider-item').forEach(closeInlineItem);
    } else {
      closeActiveDrawer();
    }
  });
  document.addEventListener('shopify:section:unload', (event) => {
    if (!(event.target instanceof Element)) return;
    suspendedSections.add(event.target);
    sectionStates.forEach((state, section) => {
      if (section === event.target || event.target.contains(section)) disposeSection(section);
    });
  }, true);
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) suspendedSections.delete(event.target);
    initialize(event.target);
  });
  document.addEventListener('product:rerender', (event) => {
    const section = event.target instanceof Element ? event.target.closest('.shopify-section--main-product') : null;
    if (section) scheduleSection(section);
  }, true);
  window.addEventListener('pageshow', () => initialize());

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }
})();


/* FENDO: keep the tailoring badge inside the first visible product image. */
(function () {
  if (window.__fendoTailoringBadgeInit) return;
  window.__fendoTailoringBadgeInit = true;

  var badgeStates = new Map();
  var updateFrame = null;
  var refreshFrame = null;

  function setClass(element, name, enabled) {
    if (element.classList.contains(name) !== enabled) element.classList.toggle(name, enabled);
  }

  function setStyle(element, name, value) {
    if (element.style.getPropertyValue(name) !== value) element.style.setProperty(name, value);
  }

  function isOnScreenVert(rect) {
    return rect.bottom > 0 && rect.top < window.innerHeight;
  }

  function removeState(gallery) {
    var state = badgeStates.get(gallery);
    if (!state) return;
    state.carousel.removeEventListener('scroll', scheduleUpdates);
    gallery.removeEventListener('load', scheduleUpdates, true);
    if (state.resizeObserver) state.resizeObserver.disconnect();
    badgeStates.delete(gallery);
  }

  function pruneDetached() {
    badgeStates.forEach(function (state, gallery) {
      if (!gallery.isConnected ||
          gallery.querySelector('[data-tailoring-badge]') !== state.badge ||
          gallery.querySelector('.product-gallery__carousel') !== state.carousel ||
          state.badge.closest('.product-gallery__media') !== state.media) {
        removeState(gallery);
      }
    });
  }

  function updateBadge(state) {
    var items = Array.from(state.carousel.querySelectorAll('.product-gallery__media'));
    var mediaIndex = items.indexOf(state.media);
    if (mediaIndex < 0) return;

    var carouselRect = state.carousel.getBoundingClientRect();
    var horizontalCarousel = state.carousel.scrollWidth > state.carousel.clientWidth + 2;
    var bestIndex = mediaIndex;

    if (horizontalCarousel) {
      var bestOverlap = -Infinity;
      bestIndex = 0;
      items.forEach(function (item, index) {
        var itemRect = item.getBoundingClientRect();
        var overlap = Math.min(itemRect.right, carouselRect.right) - Math.max(itemRect.left, carouselRect.left);
        if (overlap > bestOverlap) {
          bestOverlap = overlap;
          bestIndex = index;
        }
      });
    }

    var rect = state.media.getBoundingClientRect();
    var shouldShow = isOnScreenVert(rect) && (!horizontalCarousel || bestIndex === mediaIndex);
    setClass(state.badge, 'is-hidden', !shouldShow);
    if (!shouldShow) return;

    var mobileVideoOffset = 0;
    if (window.innerWidth < 700 && state.video && state.video.closest('.product-gallery__media') === state.media) {
      mobileVideoOffset = state.video.offsetHeight + 12;
    }

    var blockEnd = 18 + mobileVideoOffset;
    var inlineStart = Math.round(rect.left + 18);
    var inlineEnd = Math.round(window.innerWidth - rect.right + 18);
    if (!isFinite(inlineStart) || inlineStart < 18) inlineStart = 18;
    if (!isFinite(inlineEnd) || inlineEnd < 18) inlineEnd = 18;

    setStyle(state.badge, '--tailoring-badge-inline-start', inlineStart + 'px');
    setStyle(state.badge, '--tailoring-badge-inline-end', inlineEnd + 'px');
    setStyle(state.badge, '--tailoring-badge-fixed-block-end', 'calc(' + blockEnd + 'px + env(safe-area-inset-bottom, 0px))');
    setStyle(state.badge, '--tailoring-badge-docked-block-end', blockEnd + 'px');
    setClass(state.badge, 'is-ready', true);
    var isDocked = rect.bottom <= window.innerHeight - blockEnd;
    setClass(state.badge, 'is-docked-to-media', isDocked);
    if (window.innerWidth < 700 && state.video) {
      var dockedEnd = isDocked ? '18px' : '';
      var videoRect = state.video.getBoundingClientRect();
      var badgeRect = state.badge.getBoundingClientRect();
      var mediaRect = state.media.getBoundingClientRect();
      var badgeStart = isDocked ? Math.round(videoRect.left + (videoRect.width - badgeRect.width) / 2 - mediaRect.left) + 'px' : '';
      var badgeBlockEnd = isDocked ? 'calc(' + (state.video.offsetHeight + 30) + 'px)' : '';
      state.badge.style.setProperty('inset-inline-start', badgeStart, 'important');
      state.badge.style.setProperty('inset-inline-end', 'auto', 'important');
      state.badge.style.setProperty('inset-block-end', badgeBlockEnd, 'important');
      state.badge.style.setProperty('transform', 'none', 'important');
      state.video.style.setProperty('inset-inline-end', dockedEnd, 'important');
      state.video.style.setProperty('inset-block-end', isDocked ? '18px' : '', 'important');
    }
  }

  function scheduleUpdates() {
    if (updateFrame !== null) return;
    updateFrame = requestAnimationFrame(function () {
      updateFrame = null;
      pruneDetached();
      badgeStates.forEach(updateBadge);
    });
  }

  function setupGallery(gallery) {
    if (!gallery.isConnected || badgeStates.has(gallery)) return;
    var badge = gallery.querySelector('[data-tailoring-badge]');
    var carousel = gallery.querySelector('.product-gallery__carousel');
    var media = badge && badge.closest('.product-gallery__media');
    if (!badge || !carousel || !media) return;

    var state = {
      badge: badge,
      carousel: carousel,
      media: media,
      video: gallery.querySelector('[data-product-video-trigger]'),
      resizeObserver: null
    };

    badgeStates.set(gallery, state);
    carousel.addEventListener('scroll', scheduleUpdates, { passive: true });
    gallery.addEventListener('load', scheduleUpdates, true);

    if ('ResizeObserver' in window) {
      state.resizeObserver = new ResizeObserver(scheduleUpdates);
      state.resizeObserver.observe(carousel);
      state.resizeObserver.observe(media);
      state.resizeObserver.observe(badge);
    }
  }

  function refresh(root) {
    pruneDetached();
    if (root !== document && !root.isConnected) return;
    if (root instanceof Element && root.matches('product-gallery')) setupGallery(root);
    root.querySelectorAll?.('product-gallery').forEach(setupGallery);
    scheduleUpdates();
  }

  function scheduleRefresh(root) {
    if (refreshFrame !== null) return;
    refreshFrame = requestAnimationFrame(function () {
      refreshFrame = null;
      refresh(root && root.querySelectorAll ? root : document);
    });
  }

  document.addEventListener('shopify:section:load', function (event) { scheduleRefresh(event.target); });
  document.addEventListener('product:rerender', function (event) {
    scheduleRefresh(event.target instanceof Element ? event.target.closest('.shopify-section') : document);
  }, true);
  document.addEventListener('variant:change', scheduleUpdates);
  window.addEventListener('scroll', scheduleUpdates, { passive: true });
  window.addEventListener('resize', scheduleUpdates, { passive: true });
  window.addEventListener('pageshow', function () { scheduleRefresh(document); });

  var observer = new MutationObserver(function (records) {
    var relevantChange = records.some(function (record) {
      return Array.from(record.addedNodes).concat(Array.from(record.removedNodes)).some(function (node) {
        return node instanceof Element &&
          (node.matches('product-gallery, [data-tailoring-badge]') || Boolean(node.querySelector('product-gallery, [data-tailoring-badge]')));
      });
    });
    if (relevantChange) scheduleRefresh(document);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      observer.observe(document.documentElement, { childList: true, subtree: true });
      refresh(document);
    }, { once: true });
  } else {
    observer.observe(document.documentElement, { childList: true, subtree: true });
    refresh(document);
  }
})();
